import React, { FC, useEffect, useMemo, useState, useRef } from 'react';
import replyIcon from 'assets/svg/reply icon.svg';
import deleteIcon from 'assets/svg/delete icon.svg';
import pin from 'assets/svg/pin.svg';
import videoPlayIcon from 'assets/svg/video-play-icon.svg';
import Styles from 'components/chatContent/messageBox/MessageBox.module.scss';
import ProfileAvatar from 'components/profileAvatar/ProfileAvatar';
import { deleteMessage, getImages, deleteImage, updateMessageStatus, deleteThreadMessage } from 'store/services/firebase-broadcast';
import {
  attachmentPath,
  getCachedAttachmentUrl,
  getCachedAspectRatio,
  setCachedAspectRatio,
} from 'store/services/attachmentUrlCache';
import moment from 'moment';
import { formatMessageWithLineBreaks, getMessageTime, getAttachmentMediaType } from 'helpers/utils';
import { getUnreadThreadCount, getLatestThreadTime, formatLastReplyRelative, ThreadReadState } from 'store/services/threadReads';
import { useDispatch } from 'react-redux';
import { openImageViewer } from 'store/reducers/imageViewer.reducer';
import { Spinner } from 'react-bootstrap';
import { useDateTimeFormat } from 'hooks/useDateTimeFormat';

interface MessageBoxProps {
  cssClass?: string;
  item: any;
  index?: number;
  viewReplies?: Function;
  replyItems?: Function;
  actions: boolean;
  selected?: any;
  isThread?: boolean;
  type: string;
  chatsPin?: any;
  pinCount?: any;
  parentMessageId?: string;
  threadIndex?: number;
  // Ordered media descriptors for the whole conversation, so clicking an
  // attachment opens the viewer as a gallery with prev/next across all of them.
  conversationMedia?: { id: string; name?: string; type: 'image' | 'video' }[];
  /** Per-user last-seen for this parent message's thread (main chat only). */
  threadRead?: ThreadReadState;
  /** False while threadReads for the room are still loading — hide unread badge. */
  threadReadsReady?: boolean;
}

const MessageBox: FC<MessageBoxProps> = (props: MessageBoxProps) => {
  const {
    cssClass = "",
    item,
    viewReplies = () => {},
    replyItems = () => {},
    actions = false,
    selected = null,
    isThread = false,
    type,
    chatsPin = [],
    pinCount = 0,
    parentMessageId,
    threadIndex,
    conversationMedia,
    threadRead,
    threadReadsReady = true,
  } = props;
  const isMountedRef = useRef(true);

  // Resolve the attachment's URL from the session cache *synchronously*, so a remount
  // (which happens on every room switch) can seed state on the very first render. Going
  // through a promise — even an already-resolved one — leaves a render with no `src`,
  // which is exactly the image flash we're removing.
  const attachmentId: string | undefined = item?.attachments?.[0]?.id;
  const attachmentStoragePath = attachmentId ? attachmentPath(attachmentId) : undefined;
  const cachedAttachmentUrl = attachmentStoragePath
    ? getCachedAttachmentUrl(attachmentStoragePath)
    : undefined;

  const [fileType, setFileType] = useState('image');
  const [attachment, setAttachment] = useState(cachedAttachmentUrl ?? '');
  const [imageLoading, setImageLoading] = useState(!cachedAttachmentUrl);
  const [imageError, setImageError] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<number | null>(
    (cachedAttachmentUrl && getCachedAspectRatio(cachedAttachmentUrl)) || null
  );
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const dispatch = useDispatch();
  const { formatTime } = useDateTimeFormat();
  const [hover, setHover] = useState(item?.pin ?? false);
  const unreadReplies =
    !isThread && threadReadsReady && item?.threads
      ? getUnreadThreadCount(item.threads, threadRead, user?.pk)
      : 0;

  const latestThreadTime = useMemo(
    () => (!isThread && item?.threads ? getLatestThreadTime(item.threads) : 0),
    [isThread, item?.threads]
  );

  const [relativeNow, setRelativeNow] = useState(() => Date.now());
  useEffect(() => {
    if (isThread || !latestThreadTime) return undefined;
    const id = setInterval(() => setRelativeNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, [isThread, latestThreadTime]);

  const lastReplyRelative = useMemo(
    () => (latestThreadTime ? formatLastReplyRelative(latestThreadTime, relativeNow) : null),
    [latestThreadTime, relativeNow]
  );

  const hasThreadReplies = !isThread && item?.threads && item.threads.length > 0;
  const handleShowReplies = () => {
    if (viewReplies) viewReplies(true);
    if (replyItems) replyItems(item);
  };

  const getTime = (time: string) => {
    if (!time) return '';
    return formatTime(time);
  };

  const handleDelete = async () => {
    if (!selected?.pk) return;

    if (isThread && parentMessageId !== undefined && threadIndex !== undefined) {
      try {
        await deleteThreadMessage(selected.pk, parentMessageId, threadIndex);
      } catch (error) {
        console.error('Failed to delete thread message:', error);
      }
      return;
    }

    const msgPath = `${selected.pk}/messages/${item.id}`;
    const hadAttachment = item.attachments?.length > 0 && item.attachments[0]?.id;
    if (hadAttachment) {
      const imgPath = `${selected.pk}/${item.attachments[0].id}`;
      await deleteImage(imgPath).catch((error) => {
        console.warn('Failed to delete attachment file:', error);
      });
    }
    await deleteMessage(msgPath).catch((error) => {
      console.error('Failed to delete message:', error);
    });
    return '';
  };

  const handleRemoveParticipant = () => {
    if (!selected?.pk) return;
    const msgPath = `${selected.pk}/participants/${item.id}`;
    deleteMessage(msgPath).then();
    return '';
  };

  useEffect(() => {
    // Load attachments for all message types including threads
    if (!attachmentStoragePath || !attachmentId) {
      setImageLoading(false);
      return;
    }

    // Cache hit: keep what we seeded. Crucially, do NOT reset attachment/loading/ratio —
    // that reset is what made the image blink on every remount.
    const cached = getCachedAttachmentUrl(attachmentStoragePath);
    if (cached) {
      setAttachment(cached);
      setImageError(false);
      const cachedRatio = getCachedAspectRatio(cached);
      if (cachedRatio) setAspectRatio(cachedRatio);
      return;
    }

    setImageLoading(true);
    setImageError(false);
    setAspectRatio(null);

    getImages(selected.pk, attachmentId).then((e) => {
      if (isMountedRef.current && e !== undefined && e.length > 0) {
        const imageUrl = e[0];
        setAttachment(imageUrl);

        // Preload image to get aspect ratio
        const img = new Image();
        img.onload = () => {
          const ratio = img.naturalWidth / img.naturalHeight;
          // Cache it so a remount doesn't re-measure (and re-shift the layout).
          setCachedAspectRatio(imageUrl, ratio);
          if (isMountedRef.current) {
            setAspectRatio(ratio);
            // Don't set loading to false yet - wait for actual img tag to load
          }
        };
        img.onerror = () => {
          if (isMountedRef.current) {
            setImageError(true);
            setImageLoading(false);
          }
        };
        img.src = imageUrl;
      } else if (isMountedRef.current) {
        setImageLoading(false);
      }
    }).catch((error) => {
      // Silently handle missing attachment files
      console.warn('Failed to load attachment:', error);
      if (isMountedRef.current) {
        setImageError(true);
        setImageLoading(false);
      }
    });
  }, [attachmentStoragePath, attachmentId, selected.pk]);

  useEffect(() => {
    const att = item.attachments?.length > 0 ? item.attachments[0] : null;
    setFileType(getAttachmentMediaType(att));
  }, [item]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const OnPressPin = () => {
    if (!selected?.host || user.pk !== selected.host.pk) {
      return;
    }
    if ( chatsPin?.length < 3 || hover === true ) {
      const data = {
        roomId: selected.pk,
        pin: !hover,
        timeStamp: !hover ? moment().format(): "",
        messageId: item.id
      };
      updateMessageStatus(data)
    };
    setHover(!hover);
  }
  
  // Open the viewer as a gallery over the whole conversation's media, starting at
  // this message's attachment. Falls back to a single item (e.g. thread replies,
  // which don't receive `conversationMedia`).
  const openInViewer = () => {
    if (!attachment) return;
    const descriptors = (conversationMedia && conversationMedia.length > 0)
      ? conversationMedia
      : (attachmentId ? [{ id: attachmentId, name: item.attachments?.[0]?.name, type: fileType as 'image' | 'video' }] : []);
    const withId = descriptors
      .map((d) => ({ id: d.id, url: getCachedAttachmentUrl(attachmentPath(d.id)) || '', name: d.name, type: d.type }))
      .filter((it) => it.url);
    let index = withId.findIndex((it) => it.id === attachmentId);
    if (index < 0) {
      // The clicked item isn't resolved in the cache yet — show it standalone.
      withId.push({ id: attachmentId || '', url: attachment, name: item.attachments?.[0]?.name, type: fileType as 'image' | 'video' });
      index = withId.length - 1;
    }
    dispatch(openImageViewer({
      items: withId.map(({ url, name, type }) => ({ url, name, type })),
      index,
    }));
  };

  const handleImageClick = () => {
    if (attachment && fileType === 'image') {
      openInViewer();
    }
  };

  const isRoomHost =
    item.sender.isHost || item.sender.id === selected?.host?.pk;
  const isSuperHost =
    isRoomHost &&
    (item.sender.isSuperHost ?? selected?.host?.is_super_host);

  return (
    <>
      <div 
        data-message-id={item.id || item.pk}
        className={`${item.pin === true ? Styles['pinned'+pinCount]: ''}  ${Styles.message} ${isThread ? Styles.threadMessage : ''} ${cssClass} ${!actions && Styles.padding}`}
      >
        {!actions && (
          <div className={Styles.actions}>
            {!isThread && <img src={replyIcon} alt="reply icon" onClick={handleShowReplies} onKeyUp={handleShowReplies} role="button" tabIndex={0} />} 
            {(item.sender.id === user.pk || user.pk === selected.host.pk) && <img src={deleteIcon} alt="reply icon" onClick={handleDelete} onKeyUp={handleDelete} role="button" tabIndex={0} />}
            {/* {(user.pk === selected.host.pk) && <a onClick={handleRemoveParticipant} onKeyUp={handleRemoveParticipant} role="button" tabIndex={0} >Remove</a>} */}
          </div>
        )}
        <ProfileAvatar profile={item.sender.image} alt="user" className={Styles.userImage} />
        <div className={Styles.userDetails}>
          <div className={Styles.detailsInner}>
            <h5 className="my-0">{item.sender.name}</h5>
            <span className={Styles.messageTime}>{getTime(new Date(getMessageTime(item)).toISOString())}</span>
            {isSuperHost && (
              <span className={`badge text-white ${Styles.superHostBadge}`}>SUPER HOST</span>
            )}
            {isRoomHost && !isSuperHost && (
              <span className={`badge text-white ${Styles.hostBadge}`}>Host</span>
            )}
            <div onClick={OnPressPin} onKeyPress={OnPressPin} role="button" tabIndex={0}>
              <img className={`${item.pin ? Styles.pin : null} ${selected?.host && user.pk === selected.host.pk && Styles.hoverPin} `} src={pin} alt="pin"/>
            </div>
          </div>
          <p
            className={`mb-0 ${Styles.messageText}`}
            dangerouslySetInnerHTML={{ __html: formatMessageWithLineBreaks(item?.message) }}
          />
          {hasThreadReplies && (
            <div
              className={Styles.viewRepliesRow}
              onClick={handleShowReplies}
              onKeyPress={handleShowReplies}
              role="button"
              tabIndex={0}
            >
              <h6 className={`mb-0 pt-2 ${Styles.viewReplies}`}>
                <span className={Styles.viewRepliesLabel}>View Replies</span>
                {unreadReplies > 0 && (
                  <span className={Styles.newRepliesBadge}>
                    <span className={Styles.badgeHighlightNum}>{unreadReplies}</span> new
                  </span>
                )}
                {lastReplyRelative && (
                  <span className={Styles.lastReplyMeta}>
                    Last reply{' '}
                    <span className={Styles.lastReplyHighlightNum}>{lastReplyRelative.value}</span>
                    {' '}
                    {lastReplyRelative.unit} ago
                  </span>
                )}
              </h6>
            </div>
          )}
          {attachment && item.attachments && item.attachments.length > 0 && item.attachments[0] && (
            <div className="d-flex flex-column">
              <span className={Styles.uploadedImageDetails}>{item.attachments[0].name}</span>
              {fileType === 'image' ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  {imageLoading && (
                    <div 
                      className={Styles.uploadedImage}
                      style={{
                        maxWidth: '300px',
                        width: 'auto',
                        aspectRatio: aspectRatio ? `${aspectRatio}` : '4/3',
                        backgroundColor: '#f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '5px'
                      }}
                    >
                      <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                    </div>
                  )}
                  {imageError && (
                    <div 
                      className={Styles.uploadedImage}
                      style={{
                        maxWidth: '300px',
                        width: '300px',
                        aspectRatio: '4/3',
                        backgroundColor: '#f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '5px',
                        color: '#999',
                        fontSize: '12px',
                        padding: '20px'
                      }}
                    >
                      Failed to load image
                    </div>
                  )}
                  {!imageError && (
                    <img 
                      src={attachment} 
                      alt="attachment" 
                      className={Styles.uploadedImage} 
                      onClick={handleImageClick} 
                      style={{ 
                        cursor: 'pointer',
                        maxWidth: '300px',
                        height: 'auto',
                        opacity: imageLoading ? 0 : 1,
                        transition: 'opacity 0.3s ease-in-out',
                        display: imageLoading ? 'none' : 'block'
                      }} 
                      onLoad={() => setImageLoading(false)}
                      onError={() => {
                        setImageError(true);
                        setImageLoading(false);
                      }}
                    />
                  )}
                </div>
              ) : (
                <div className={Styles.uploadedVideoWrapper}>
                  {/* #t=0.1 + preload=metadata paints the first frame as a poster
                      instead of a black canvas; playback (from ~0.1s) is unaffected. */}
                  <video
                    className={Styles.uploadedVideo}
                    controls
                    preload="metadata"
                    src={`${attachment}#t=0.1`}
                  />
                  <button
                    type="button"
                    className={Styles.videoModalBtn}
                    aria-label="Open video"
                    onClick={(e) => {
                      e.stopPropagation();
                      openInViewer();
                    }}
                  >
                    <img src={videoPlayIcon} alt="" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
export default React.memo(MessageBox);
